import React, { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogContent,
  TextField,
  Box,
  IconButton,
} from "@mui/material";
import ArgonButton from "components/ArgonButton";
import ArgonTypography from "components/ArgonTypography";
import EditIcon from "@mui/icons-material/Edit";
import { addCategory, editCategory } from "services/categoriesService";
import ArgonBox from "components/ArgonBox";
import { Image } from "@mui/icons-material";
import { toast } from "react-toastify";

const CategoryModal = ({ open, onClose, category, onSuccess }) => {
  const isEdit = Boolean(category);

  const [form, setForm] = useState({
    name: "",
    icon: null, // store actual file
  });
  const [iconPreview, setIconPreview] = useState(null);
  const [loading, setLoading] = useState(false);

  const fileInputRef = useRef();

  useEffect(() => {
    if (category) {
      setForm({ name: category.name || "", icon: null });
      setIconPreview(category.icon || null);
    } else {
      setForm({ name: "", icon: null });
      setIconPreview(null);
    }
  }, [category, open]);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleEditImageClick = () => {
    fileInputRef.current.click();
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      handleChange("icon", file);
      const reader = new FileReader();
      reader.onload = (ev) => {
        setIconPreview(ev.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async () => {
    if (!form.name.trim()) {
      toast.error("Category name is required");
      return;
    }

    const payload = new FormData();
    payload.append("name", form.name.trim());
    if (form.icon) {
      payload.append("icon", form.icon);
    }

    try {
      setLoading(true);

      const response = isEdit
        ? await editCategory(category._id, payload)
        : await addCategory(payload);

      if (response.success) {
        onSuccess(response.data);
        onClose();
      } else {
        toast.error(response.message || "Failed to save category");
      }
    } catch (err) {
      toast.error(err.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        style: {
          borderRadius: "16px",
          padding: "40px 32px",
          textAlign: "center",
        },
      }}
    >
      <DialogContent>
        <input
          type="file"
          accept="image/jpeg, image/png, image/jpg"
          ref={fileInputRef}
          style={{ display: "none" }}
          onChange={handleImageChange}
        />

        <ArgonTypography variant="h4" mb={3}>
          {isEdit ? "Edit Category" : "Add New Category"}
        </ArgonTypography>

        {/* Image Preview */}
        <Box
          sx={{
            width: 72,
            height: 72,
            mx: "auto",
            mb: 3,
            position: "relative",
            borderRadius: "12px",
            backgroundColor: "#F1F5F9",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <ArgonBox display="flex" alignItems="center" justifyContent="center">
            {iconPreview ? (
              <img
                src={iconPreview}
                alt="icon"
                style={{
                  borderRadius: 5,
                  width: "60%",
                  height: "60%",
                }}
              />
            ) : (
              <Image fontSize="large" color="primary" />
            )}
          </ArgonBox>
          <IconButton
            onClick={handleEditImageClick}
            sx={{
              position: "absolute",
              bottom: -10,
              right: -10,
              bgcolor: "#2d3f50",
              border: "1px solid #2d3f50",
              borderRadius: "8px",
              width: 25,
              height: 25,
            }}
          >
            <EditIcon fontSize="small" color="white" />
          </IconButton>
        </Box>

        {/* Category Name Input */}
        <Box mb={3} textAlign="left">
          <ArgonTypography variant="h6" fontWeight="bold" mb={1}>
            Category Title
          </ArgonTypography>
          <TextField
            fullWidth
            value={form.name}
            onChange={(e) => handleChange("name", e.target.value)}
            placeholder="Enter Category Title"
            disabled={loading}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "10px",
                backgroundColor: "#fff",
              },
              "& input::placeholder": {
                color: "#94A3B8",
                fontSize: 14,
              },
              "& .MuiInputBase-input": {
                fontSize: 14,
                fontWeight: 500,
                color: "#172a4d",
              },
            }}
          />
        </Box>

        {/* Buttons */}
        <Box display="flex" justifyContent="center" gap={2}>
          <ArgonButton
            variant="outlined"
            color="secondary"
            onClick={onClose}
            disabled={loading}
            sx={{ minWidth: 100, backgroundColor: "#ffff", color: "#2d3f50" }}
          >
            Cancel
          </ArgonButton>
          <ArgonButton
            variant="contained"
            onClick={handleSubmit}
            disabled={loading}
            sx={{ minWidth: 100, backgroundColor: "#2d3f50", color: "#ffff" }}
          >
            {loading ? "Saving..." : isEdit ? "Update" : "Add"}
          </ArgonButton>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default CategoryModal;
